// Importar la imagen del logo
import logoArca from '../assets/images/logo-arca.jpg';
import QRCode from 'qrcode';

const CBTE_TIPO_INFO = {
  1: { letra: 'A', codigo: '001', etiqueta: 'FACTURA', tipoId: 1, esNotaCredito: false },
  6: { letra: 'B', codigo: '006', etiqueta: 'FACTURA', tipoId: 2, esNotaCredito: false },
  11: { letra: 'C', codigo: '011', etiqueta: 'FACTURA', tipoId: 3, esNotaCredito: false },
  51: { letra: 'M', codigo: '051', etiqueta: 'FACTURA', tipoId: 7, esNotaCredito: false },
  3: { letra: 'A', codigo: '003', etiqueta: 'NOTA DE CREDITO', tipoId: 1, esNotaCredito: true },
  8: { letra: 'B', codigo: '008', etiqueta: 'NOTA DE CREDITO', tipoId: 2, esNotaCredito: true },
  13: { letra: 'C', codigo: '013', etiqueta: 'NOTA DE CREDITO', tipoId: 3, esNotaCredito: true },
  53: { letra: 'M', codigo: '053', etiqueta: 'NOTA DE CREDITO', tipoId: 7, esNotaCredito: true },
};

const FACTURA_ID_TO_CBTE = {
  1: 1,
  2: 6,
  3: 11,
  7: 51,
};

const AFIP_QR_VERSION = 1;
const AFIP_QR_BASE_URL = 'https://www.afip.gob.ar/fe/qr/';

function sanitizeDigits(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).replace(/\D/g, '');
}

function parseInteger(value) {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  const digits = sanitizeDigits(value);
  if (!digits) {
    return undefined;
  }
  const parsed = Number(digits);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : undefined;
}

function parseDecimal(value, precision = 2) {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number(value.toFixed(precision));
  }
  const normalized = String(value).trim().replace(/\s+/g, '').replace(/,/g, '.');
  if (!normalized) {
    return undefined;
  }
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    const digits = sanitizeDigits(normalized);
    if (!digits) {
      return undefined;
    }
    const fallback = Number(digits);
    return Number.isFinite(fallback) ? Number(fallback.toFixed(precision)) : undefined;
  }
  return Number(parsed.toFixed(precision));
}

function formatDateForQr(value) {
  const parsed = parseAfipDate(value);
  const date = parsed || new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function obtenerCuitEmisor(parametrosEmisorAfip = {}, payload = {}, data = {}) {
  const candidates = [
    parametrosEmisorAfip.afipsdk_cuit,
    parametrosEmisorAfip.cuit,
    payload.CuitEmisor,
    payload.cuitEmisor,
    data.CuitEmisor,
    data.cuitEmisor,
  ];
  for (const candidate of candidates) {
    const digits = sanitizeDigits(candidate);
    if (digits) {
      const parsed = Number(digits);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return undefined;
}

function obtenerMoneda(payload = {}, data = {}) {
  const candidates = [
    payload.MonId,
    payload.moneda,
    payload.Moneda,
    data.MonId,
    data.moneda,
    data.Moneda,
  ];
  const raw = candidates.find(value => value !== undefined && value !== null && String(value).trim() !== '');
  if (!raw) {
    return 'PES';
  }
  return String(raw).toUpperCase().slice(0, 3) || 'PES';
}

function obtenerCotizacion(payload = {}, data = {}) {
  const candidates = [
    payload.MonCotiz,
    payload.cotizacion,
    payload.cotizacionMoneda,
    data.MonCotiz,
    data.cotizacion,
  ];
  for (const candidate of candidates) {
    const parsed = parseDecimal(candidate, 6);
    if (parsed !== undefined && parsed > 0) {
      return parsed;
    }
  }
  return 1;
}

function obtenerTipoCodAut(payload = {}, data = {}) {
  const candidates = [
    payload.tipoCodAut,
    payload.TipoCodAut,
    payload.tipoCodAutorizacion,
    payload.tipoCodAutorizacionAfip,
    data.tipoCodAut,
    data.TipoCodAut,
    data.tipoCodAutorizacion,
    data.tipoCodAutorizacionAfip,
  ];
  const raw = candidates.find(value => value !== undefined && value !== null && String(value).trim() !== '');
  if (raw) {
    const normalized = String(raw).trim().toUpperCase();
    if (normalized === 'A' || normalized === 'E') {
      return normalized;
    }
    if (normalized.includes('CAEA')) {
      return 'A';
    }
    if (normalized.includes('CAE')) {
      return 'E';
    }
  }
  if (payload.CAEA || data.CAEA) {
    return 'A';
  }
  return 'E';
}

function obtenerCodAutorizacion(infoComprobante = {}, payload = {}, data = {}) {
  const candidates = [
    infoComprobante.cae,
    payload.CAE,
    payload.cae,
    data.CAE,
    data.cae,
    payload.codAut,
    data.codAut,
  ];
  for (const candidate of candidates) {
    const digits = sanitizeDigits(candidate);
    if (digits) {
      const parsed = Number(digits);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return undefined;
}

function obtenerImporteTotal(payload = {}, data = {}, fallbackTotal = 0) {
  const candidates = [
    payload.ImpTotal,
    payload.impTotal,
    payload.total,
    data.ImpTotal,
    data.impTotal,
    data.total,
  ];
  for (const candidate of candidates) {
    const parsed = parseDecimal(candidate, 2);
    if (parsed !== undefined) {
      return parsed;
    }
  }
  return Number(parseDecimal(fallbackTotal, 2) ?? 0);
}

function extraerDocumentoReceptor(payload = {}, data = {}) {
  const tipoCandidates = [
    payload.DocTipo,
    payload.docTipo,
    payload.tipoDocRec,
    payload.TipoDocRec,
    payload.tipoDocumentoReceptor,
    data.DocTipo,
    data.docTipo,
    data.tipoDocRec,
    data.TipoDocRec,
  ];
  let docTipo = tipoCandidates.find(value => value !== undefined && value !== null && String(value).trim() !== '');

  const numeroCandidates = [
    payload.DocNro,
    payload.docNro,
    payload.nroDocRec,
    payload.NroDocRec,
    payload.DniCuitCliente,
    payload.dniCuitCuil,
    payload.documentoCliente,
    data.DocNro,
    data.docNro,
    data.nroDocRec,
    data.NroDocRec,
    data.DniCuitCliente,
  ];
  let docNumero = numeroCandidates.find(value => value !== undefined && value !== null && String(value).trim() !== '');

  const cleanedNumber = sanitizeDigits(docNumero);

  if (!docTipo) {
    if (cleanedNumber.length === 11) {
      docTipo = 80;
    } else if (cleanedNumber.length >= 7) {
      docTipo = 96;
    }
  }

  if (!docTipo) {
    return { docTipo: undefined, docNro: undefined };
  }

  const docTipoNumber = Number(docTipo);
  if (!Number.isFinite(docTipoNumber)) {
    return { docTipo: undefined, docNro: undefined };
  }

  if (!cleanedNumber && docTipoNumber === 99) {
    return { docTipo: 99, docNro: 0 };
  }

  if (!cleanedNumber) {
    return { docTipo: undefined, docNro: undefined };
  }

  const docNroNumber = Number(cleanedNumber);
  if (!Number.isFinite(docNroNumber) || (docNroNumber === 0 && docTipoNumber !== 99)) {
    return { docTipo: undefined, docNro: undefined };
  }

  return { docTipo: docTipoNumber, docNro: docTipoNumber === 99 ? 0 : docNroNumber };
}

function encodeToBase64(str) {
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    return window.btoa(unescape(encodeURIComponent(str)));
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(str, 'utf8').toString('base64');
  }
  throw new Error('Base64 encoding no disponible en este entorno');
}

async function generarCodigoQrAfip({ infoComprobante, total, payload = {}, data = {}, parametrosEmisorAfip = {} }) {
  if (!infoComprobante) {
    return null;
  }

  const fecha = formatDateForQr(
    data.CbteFch ??
    data.cbteFch ??
    payload.CbteFch ??
    payload.cbteFch ??
    infoComprobante.fechaEmision,
  );

  const cuitEmisor = obtenerCuitEmisor(parametrosEmisorAfip, payload, data);
  const puntoVenta = parseInteger(
    payload.PtoVta ??
    payload.puntoVenta ??
    data.PtoVta ??
    data.ptoVta ??
    infoComprobante.puntoVenta,
  );
  const tipoComprobante = parseInteger(
    data.CbteTipo ??
    data.cbteTipo ??
    payload.CbteTipo ??
    payload.cbteTipo ??
    infoComprobante.cbteTipo,
  );
  const numeroComprobante = parseInteger(
    data.CbteNro ??
    data.cbteNro ??
    payload.CbteHasta ??
    payload.CbteDesde ??
    payload.numeroFactura ??
    infoComprobante.numero,
  );
  const importeTotal = obtenerImporteTotal(payload, data, total);

  const { docTipo, docNro } = extraerDocumentoReceptor(payload, data);
  const tipoCodAut = obtenerTipoCodAut(payload, data);
  const codAutorizacion = obtenerCodAutorizacion(infoComprobante, payload, data);

  if (
    !cuitEmisor ||
    puntoVenta === undefined ||
    tipoComprobante === undefined ||
    numeroComprobante === undefined ||
    importeTotal === undefined ||
    !tipoCodAut ||
    !codAutorizacion
  ) {
    console.warn('No se pudo generar datos completos para el QR AFIP', {
      cuitEmisor,
      puntoVenta,
      tipoComprobante,
      numeroComprobante,
      importeTotal,
      tipoCodAut,
      codAutorizacion,
    });
    return null;
  }

  const qrPayload = {
    ver: AFIP_QR_VERSION,
    fecha,
    cuit: cuitEmisor,
    ptoVta: puntoVenta,
    tipoCmp: tipoComprobante,
    nroCmp: numeroComprobante,
    importe: Number(parseDecimal(importeTotal, 2)),
    moneda: obtenerMoneda(payload, data),
    ctz: obtenerCotizacion(payload, data),
    tipoCodAut,
    codAut: codAutorizacion,
  };

  if (docTipo !== undefined && docNro !== undefined) {
    qrPayload.tipoDocRec = docTipo;
    qrPayload.nroDocRec = docNro;
  }

  const qrPayloadString = JSON.stringify(qrPayload);
  const qrPayloadBase64 = encodeToBase64(qrPayloadString);
  const qrUrl = `${AFIP_QR_BASE_URL}?p=${qrPayloadBase64}`;
  const qrDataUrl = await QRCode.toDataURL(qrUrl, {
    errorCorrectionLevel: 'M',
    margin: 1,
    width: 220,
  });

  return {
    dataUrl: qrDataUrl,
    url: qrUrl,
    payload: qrPayload,
  };
}

function padLeft(value, length, fill = '0') {
  if (value === null || value === undefined) {
    return ''.padStart(length, fill);
  }
  if (typeof value === 'string' && value.trim() === '') {
    return value.padStart(length, fill);
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value).padStart(length, fill);
  }
  return String(Math.trunc(numericValue)).padStart(length, fill);
}

function parseAfipDate(value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  const str = String(value).trim();
  if (!str) {
    return null;
  }
  if (/^\d{8}$/.test(str)) {
    const year = Number(str.slice(0, 4));
    const month = Number(str.slice(4, 6)) - 1;
    const day = Number(str.slice(6, 8));
    const date = new Date(year, month, day);
    return Number.isNaN(date.valueOf()) ? null : date;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const iso = str.length === 10 ? str + 'T00:00:00' : str;
    const date = new Date(iso);
    return Number.isNaN(date.valueOf()) ? null : date;
  }
  const timestamp = Date.parse(str);
  if (Number.isNaN(timestamp)) {
    return null;
  }
  return new Date(timestamp);
}

function formatAfipDate(value, useTodayFallback = true) {
  const parsed = parseAfipDate(value);
  if (!parsed) {
    if (useTodayFallback) {
      return new Date().toLocaleDateString('es-AR');
    }
    return '';
  }
  return parsed.toLocaleDateString('es-AR');
}

function mapCondicionIva(condicion) {
  if (condicion === null || condicion === undefined) {
    return null;
  }
  const value = String(condicion).toUpperCase();
  const map = {
    '5': 'Consumidor Final',
    '99': 'Consumidor Final',
    'CONSUMIDOR FINAL': 'Consumidor Final',
    'IVA RESPONSABLE INSCRIPTO': 'IVA RESPONSABLE INSCRIPTO',
    'RESPONSABLE INSCRIPTO': 'IVA RESPONSABLE INSCRIPTO',
    'RI': 'IVA RESPONSABLE INSCRIPTO',
    'MONOTRIBUTO': 'Monotributista',
    'MONOTRIBUTISTA': 'Monotributista',
  };
  return map[value] || condicion;
}

function getDatosCliente(payload = {}) {
  return {
    condicionIva: mapCondicionIva(
      payload.condicionIvaCliente ||
      payload.condicionIVACliente ||
      payload.condicionIVA ||
      payload.CondicionIVAReceptorDescripcion ||
      payload.CondicionIVAReceptorId
    ) || 'IVA RESPONSABLE INSCRIPTO',
    condicionVenta: payload.condicionVenta || payload.condicionVentaDescripcion || 'CONTADO',
    domicilio: payload.direccionCliente || payload.domicilioCliente || payload.direccion || '',
  };
}

function getComprobanteInfo(payload = {}, data = {}) {
  const tipoId = payload.idTipoFacturaTransaccion ?? payload.idTipoFactura ?? payload.tipoFacturaId;
  const rawCbteTipo = payload.CbteTipo ?? data.CbteTipo ?? data.cbteTipo ?? (tipoId ? FACTURA_ID_TO_CBTE[tipoId] : undefined);
  const cbteTipo = rawCbteTipo !== undefined ? Number(rawCbteTipo) : undefined;
  const baseInfo = (cbteTipo && CBTE_TIPO_INFO[cbteTipo]) || (tipoId && CBTE_TIPO_INFO[FACTURA_ID_TO_CBTE[tipoId]]) || CBTE_TIPO_INFO[6];

  const puntoVenta = padLeft(payload.PtoVta ?? data.PtoVta ?? data.ptoVta ?? 1, 5);
  const numero = padLeft(
    data.cbteNro ??
    data.CbteNro ??
    payload.CbteHasta ??
    payload.CbteDesde ??
    payload.numeroFactura ??
    payload.cbteNro ??
    0,
    8,
  );

  const fechaEmision = formatAfipDate(payload.CbteFch ?? data.CbteFch ?? data.cbteFch, true);
  const periodoDesde = formatAfipDate(
    payload.FchServDesde ??
    payload.FechaServicioDesde ??
    payload.periodoDesde ??
    payload.PeriodoDesde,
    false,
  ) || fechaEmision;
  const periodoHasta = formatAfipDate(
    payload.FchServHasta ??
    payload.FechaServicioHasta ??
    payload.periodoHasta ??
    payload.PeriodoHasta,
    false,
  ) || fechaEmision;
  const vencimientoCae = formatAfipDate(
    data.vto ??
    data.Vto ??
    data.vencimientoCae ??
    data.VencimientoCAE,
    false,
  );

  return {
    ...baseInfo,
    cbteTipo: cbteTipo ?? FACTURA_ID_TO_CBTE[baseInfo.tipoId],
    puntoVenta,
    numero,
    fechaEmision,
    periodoDesde,
    periodoHasta,
    cae: data.cae ?? data.CAE ?? '',
    vencimientoCae,
    fechaVencimiento: formatAfipDate(
      payload.FechaVencimiento ??
      payload.fechaVencimiento ??
      data.fechaVencimiento ??
      data.FechaVencimiento,
      false,
    ) || fechaEmision,
    mostrarIva: [1, 2, 7].includes(baseInfo.tipoId),
    nombreArchivoBase: baseInfo.esNotaCredito ? 'Nota_Credito' : 'Factura',
  };
}

function formatARS(number) {
  return Number(number || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

async function obtenerParametrosEmisorAfip(getParametrosGlobales) {
  try {
    const parametrosEmisorAfip = await getParametrosGlobales([
      'afipsdk_condicion_iva',
      'afipsdk_domicilio',
      'afipsdk_email',
      'afipsdk_inicio_actividades',
      'afipsdk_telefono',
      'afipsdk_razon_social',
      'afipsdk_cuit',
    ]);
    return parametrosEmisorAfip;
  } catch (error) {
    console.error('Error al obtener parametros del emisor AFIP:', error);
    return {
      afipsdk_razon_social: 'Empresa Demo',
      afipsdk_cuit: '20285678499',
      afipsdk_condicion_iva: 'IVA RESPONSABLE INSCRIPTO',
      afipsdk_domicilio: 'Direccion Demo',
      afipsdk_telefono: '011-1234-5678',
      afipsdk_email: 'demo@empresa.com',
      afipsdk_inicio_actividades: '01/01/2020',
    };
  }
}
function calcularLineas(items) {
  return items.map(item => {
    const precioConIva = Number(item.precio || 0);
    const cantidad = Number(item.cantidad || 0);
    const descuentoFactor = item.porcentajeDescuento ? (1 - item.porcentajeDescuento / 100) : 1;

    let porcentajeIva = 21;
    if (item.impuestoData && item.impuestoData.porcentaje !== undefined) {
      porcentajeIva = item.impuestoData.porcentaje;
    } else if (item.ivaPercentage !== undefined) {
      porcentajeIva = item.ivaPercentage;
    }

    const ivaRate = porcentajeIva / 100;
    const subtotalConDescuento = precioConIva * cantidad * descuentoFactor;
    const baseUnit = precioConIva / (1 + ivaRate);
    const subtotalBase = baseUnit * cantidad * descuentoFactor;
    const ivaSubtotal = subtotalConDescuento - subtotalBase;

    return {
      ...item,
      cantidad,
      porcentajeIva,
      subtotalConDescuento,
      baseUnit,
      subtotalBase,
      ivaSubtotal,
      descuentoFactor,
    };
  });
}

function calcularTotales(lines) {
  const impuestosPorAlicuota = {};
  let total = 0;

  lines.forEach(line => {
    const { porcentajeIva, subtotalBase, ivaSubtotal, subtotalConDescuento } = line;

    if (!impuestosPorAlicuota[porcentajeIva]) {
      impuestosPorAlicuota[porcentajeIva] = {
        baseImponible: 0,
        importeIva: 0,
      };
    }

    impuestosPorAlicuota[porcentajeIva].baseImponible += subtotalBase;
    impuestosPorAlicuota[porcentajeIva].importeIva += ivaSubtotal;

    total += subtotalConDescuento;
  });

  const totalBase = Object.values(impuestosPorAlicuota).reduce((acc, item) => acc + item.baseImponible, 0);
  const totalIva = Object.values(impuestosPorAlicuota).reduce((acc, item) => acc + item.importeIva, 0);

  return { total, totalBase, totalIva };
}

function buildItemsHtml(lines, mostrarColumnasIva) {
  return lines.map(line => `
    <tr>
      <td class="text-left">${line.codigo || ''}</td>
      <td class="text-left">${line.descripcion || ''}</td>
      <td class="text-right">${formatARS(line.cantidad)}</td>
      <td class="text-right">${formatARS(line.baseUnit)}</td>
      <td class="text-center">${formatARS(line.porcentajeDescuento || 0)}</td>
      <td class="text-center">${formatARS(line.subtotalBase)}</td>
      ${mostrarColumnasIva ? `
        <td class="text-right">${formatARS(line.porcentajeIva)}</td>
        <td class="text-right">${formatARS(line.subtotalConDescuento)}</td>
      ` : `
        <td class="text-right">-</td>
        <td class="text-right">-</td>
      `}
    </tr>
  `).join('');
}
export async function generarFacturaHtml(data, payload, getParametrosGlobales) {
  const parametrosEmisorAfip = await obtenerParametrosEmisorAfip(getParametrosGlobales);
  const infoComprobante = getComprobanteInfo(payload, data);
  const datosCliente = getDatosCliente(payload);
  const items = Array.isArray(payload?.ItemsList) ? payload.ItemsList : [];
  const lines = calcularLineas(items);
  const { total, totalBase, totalIva } = calcularTotales(lines);
  const mostrarColumnasIva = infoComprobante.mostrarIva;
  const qrInfo = await generarCodigoQrAfip({
    infoComprobante,
    total,
    payload,
    data,
    parametrosEmisorAfip,
  });

  const datosEmpresa = {
    razonSocial: parametrosEmisorAfip.afipsdk_razon_social,
    cuit: parametrosEmisorAfip.afipsdk_cuit,
    condicionIva: parametrosEmisorAfip.afipsdk_condicion_iva,
    domicilio: parametrosEmisorAfip.afipsdk_domicilio,
    telefono: parametrosEmisorAfip.afipsdk_telefono,
    email: parametrosEmisorAfip.afipsdk_email,
    inicioActividades: parametrosEmisorAfip.afipsdk_inicio_actividades,
    ingresosBrutos: 'EXENTO',
  };

  const datosFactura = {
    puntoVenta: infoComprobante.puntoVenta,
    numero: infoComprobante.numero,
    fecha: infoComprobante.fechaEmision,
    fechaVencimiento: infoComprobante.fechaVencimiento,
    periodoDesde: infoComprobante.periodoDesde,
    periodoHasta: infoComprobante.periodoHasta,
    cae: infoComprobante.cae,
    vencimientoCae: infoComprobante.vencimientoCae,
  };

  const itemsHtml = buildItemsHtml(lines, mostrarColumnasIva);

  const htmlTemplate = `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${infoComprobante.etiqueta} ${infoComprobante.letra} - ${datosFactura.puntoVenta}-${datosFactura.numero}</title>
  <style>
    * {
      box-sizing: border-box;
      font-family: 'Arial', 'Helvetica', sans-serif;
    }
    body {
      width: 21cm;
      font-size: 12px;
      margin: 0;
      padding: 15px;
      background-color: #ffffff;
      color: #333333;
    }
    .wrapper {
      border: 1px solid #000000;
      padding: 10px;
      background-color: #ffffff;
    }
    .text-left { text-align: left; }
    .text-center { text-align: center; }
    .text-right { text-align: right; }
    .bold { font-weight: bold; }
    .inline-block { display: inline-block; }
    .flex { display: flex; flex-wrap: wrap; }
    .no-margin { margin: 0; }
    .relative { position: relative; }
    .space-around { justify-content: space-around; }
    .space-between { justify-content: space-between; }
    .w50 { width: 50%; }
    th {
      border: 1px solid #000000;
      background-color: #f0f0f0;
      color: #000000;
      padding: 8px;
      font-size: 11px;
      font-weight: bold;
    }
    td {
      padding: 6px;
      font-size: 11px;
      border: 1px solid #000000;
      background-color: #ffffff;
    }
    table {
      border-collapse: collapse;
      width: 100%;
    }
    .header-title {
      font-size: 28px;
      font-weight: bold;
      color: #000000;
      text-align: center;
      margin-bottom: 5px;
    }
    .floating-mid {
      left: 0;
      right: 0;
      margin-left: auto;
      margin-right: auto;
      width: 60px;
      position: absolute;
      top: 3px;
      background: #fff;
      border: 1px solid #000000;
      padding: 2px;
    }
    .qr-placeholder {
      width: 140px;
      height: 140px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      background-color: transparent;
      overflow: hidden;
    }
    .afip-logo {
      width: 120px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #ffffff;
      margin-bottom: 10px;
      overflow: hidden;
    }
    .afip-logo img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
    @media print {
      body { margin: 0; padding: 0; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="wrapper text-center bold" style="width:100%;border-bottom:0;background-color:#f0f0f0;color:black;">
    ORIGINAL
  </div>

  <div class="flex relative">
    <div class="wrapper inline-block w50 flex" style="border-right:0;">
      <h3 class="header-title" style="width:100%;">${datosEmpresa.razonSocial}</h3>
      <p style="font-size:12px;line-height:1.4;margin-bottom:0;align-self:flex-end;">
        <b>Razon Social:</b> ${datosEmpresa.razonSocial}<br />
        <b>Domicilio Comercial:</b> ${datosEmpresa.domicilio}<br />
        <b>Condicion frente al IVA:</b> ${datosEmpresa.condicionIva}
      </p>
    </div>
    <div class="wrapper inline-block w50">
      <h3 class="header-title">${infoComprobante.etiqueta}</h3>
      <p style="font-size:12px;line-height:1.4;margin-bottom:0;">
        <b>Punto de Venta: ${datosFactura.puntoVenta} Comp. Nro: ${datosFactura.numero}</b><br />
        <b>Fecha de Emision:</b> ${datosFactura.fecha}<br />
        <b>CUIT:</b> ${datosEmpresa.cuit}<br />
        <b>Ingresos Brutos:</b> ${datosEmpresa.ingresosBrutos}<br />
        <b>Inicio de Actividades:</b> ${datosEmpresa.inicioActividades}
      </p>
    </div>
    <div class="wrapper floating-mid">
      <h3 class="no-margin text-center" style="font-size:28px;color:#000000;">${infoComprobante.letra}</h3>
      <h5 class="no-margin text-center" style="font-size:9px;color:#666666;">COD. ${infoComprobante.codigo}</h5>
    </div>
  </div>

  <div class="wrapper flex space-around" style="margin-top:1px;">
    <span><b>Periodo Desde:</b> ${datosFactura.periodoDesde}</span>
    <span><b>Periodo Hasta:</b> ${datosFactura.periodoHasta}</span>
    <span><b>Fecha Vencimiento Pago:</b> ${datosFactura.fechaVencimiento}</span>
  </div>

  <div class="wrapper" style="margin-top:10px;font-size:12px;">
    <div class="flex" style="margin-bottom:10px;">
      <span style="width:30%"><b>CUIT:</b> ${payload.DniCuitCliente || ''}</span>
      <span><b>Apellido y Nombre / Razon Social:</b> ${payload.ApellidoNombre || ''}</span>
    </div>
    <div class="flex" style="flex-wrap:nowrap;margin-bottom:5px;">
      <span style="width:70%"><b>Condicion frente al IVA:</b> ${datosCliente.condicionIva}</span>
      <span><b>Domicilio:</b> ${datosCliente.domicilio}</span>
    </div>
    <div class="flex">
      <span><b>Condicion de venta:</b> ${datosCliente.condicionVenta}</span>
    </div>
  </div>

  <table style="margin-top:5px;">
    <thead>
      <th class="text-left">Codigo</th>
      <th class="text-left">Producto / Servicio</th>
      <th>Cantidad</th>
      <th>Precio Unit.</th>
      <th>% Bonif</th>
      <th>Subtotal</th>
      ${mostrarColumnasIva ? '<th>% IVA</th><th>Subtotal c/IVA</th>' : '<th>-</th><th>-</th>'}
    </thead>
    <tbody>
      ${itemsHtml}
    </tbody>
  </table>

  <div class="wrapper" style="margin-top:20px;">
    <div class="flex space-between">
      <div style="width:55%">
        <p class="bold" style="color:#000000;font-size:14px;">Otros tributos</p>
        <table>
          <thead>
            <th>Descripcion</th>
            <th>Detalle</th>
            <th class="text-right">Alic. %</th>
            <th class="text-right">Importe</th>
          </thead>
          <tbody>
            <tr>
              <td>Per./Ret. Ganancias</td>
              <td></td>
              <td></td>
              <td class="text-right">${formatARS(0)}</td>
            </tr>
            <tr>
              <td>Per./Ret. IVA</td>
              <td></td>
              <td></td>
              <td class="text-right">${formatARS(0)}</td>
            </tr>
            <tr>
              <td>Impuestos Internos</td>
              <td></td>
              <td></td>
              <td class="text-right">${formatARS(0)}</td>
            </tr>
            <tr>
              <td>Impuestos Municipales</td>
              <td></td>
              <td></td>
              <td class="text-right">${formatARS(0)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style="width:40%;margin-top:20px;" class="wrapper">
        ${mostrarColumnasIva ? `
          <div class="flex" style="justify-content:space-between;">
            <span class="bold">Importe Neto Gravado:</span>
            <span class="bold">${formatARS(totalBase)}</span>
          </div>
          <div class="flex" style="justify-content:space-between;">
            <span class="bold">IVA:</span>
            <span class="bold">${formatARS(totalIva)}</span>
          </div>
        ` : `
          <div class="flex" style="justify-content:space-between;">
            <span class="bold">Importe Neto Gravado:</span>
            <span class="bold">${formatARS(0)}</span>
          </div>
          <div class="flex" style="justify-content:space-between;">
            <span class="bold">Importe No Gravado:</span>
            <span class="bold">${formatARS(total)}</span>
          </div>
        `}
        <div class="flex" style="justify-content:space-between;">
          <span class="bold">Otros Tributos:</span>
          <span class="bold">${formatARS(0)}</span>
        </div>
        <div class="flex" style="justify-content:space-between;">
          <span class="bold" style="font-size:16px;">Importe Total:</span>
          <span class="bold" style="font-size:16px;">${formatARS(total)}</span>
        </div>
      </div>
    </div>

    <div class="flex relative" style="margin-top:20px;">
      <div class="qr-container" style="padding:0 20px 20px 20px;width:25%;display:flex;flex-direction:column;align-items:center;gap:6px;">
        <div class="qr-placeholder">
          ${qrInfo?.dataUrl ? `<img src="${qrInfo.dataUrl}" alt="QR AFIP" style="width:100%;height:100%;object-fit:contain;" />` : '<span style="font-size:9px;color:#666;">QR no disponible</span>'}
        </div>
      </div>
      <div style="padding-left:10px;padding-right:10px;width:45%;">
        <div class="afip-logo">
          <img src="${logoArca}" alt="ARCA Logo" />
        </div>
        <h4 class="bold" style="color:#000000;font-style:italic;">Comprobante Autorizado</h4>
        <p class="bold" style="font-size:9px;color:#666666;font-style:italic;">Esta administracion federal no se responsabiliza por los datos ingresados en el detalle de la operacion</p>
      </div>
      <div class="flex" style="align-self:flex-start;width:30%;justify-content:flex-end;gap:10px;text-align:right;">
        <div style="display:flex;flex-direction:column;gap:6px;align-items:flex-end;">
          <span class="bold" style="color:#000000;">CAE Nro:</span>
          <span class="bold" style="color:#000000;">Vencimiento CAE:</span>
        </div>
        <div style="display:flex;flex-direction:column;gap:6px;align-items:flex-start;">
          <span style="color:#000000;font-weight:bold;">${datosFactura.cae}</span>
          <span style="color:#000000;font-weight:bold;">${datosFactura.vencimientoCae || ''}</span>
        </div>
      </div>
    </div>
  </div>
</body>
</html>
`;

  return htmlTemplate;
}
export async function abrirFacturaHtml(data, payload, getParametrosGlobales) {
  try {
    await descargarFacturaPdf(data, payload, getParametrosGlobales);
  } catch (error) {
    console.error('Error al generar PDF, fallback a HTML:', error);
    const htmlContent = await generarFacturaHtml(data, payload, getParametrosGlobales);
    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(htmlBlob);
    window.open(url, '_blank');
  }
}

export async function descargarFacturaHtml(data, payload, getParametrosGlobales) {
  const htmlContent = await generarFacturaHtml(data, payload, getParametrosGlobales);
  const infoComprobante = getComprobanteInfo(payload, data);
  const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(htmlBlob);

  const a = document.createElement('a');
  a.href = url;
  const baseFileName = `${infoComprobante.nombreArchivoBase}_${infoComprobante.letra}_${infoComprobante.puntoVenta}_${infoComprobante.numero}`;
  a.download = `${baseFileName}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function descargarFacturaPdf(data, payload, getParametrosGlobales) {
  try {
    const { jsPDF } = await import('jspdf');
    const html2canvas = (await import('html2canvas')).default;

    const htmlContent = await generarFacturaHtml(data, payload, getParametrosGlobales);
    const infoComprobante = getComprobanteInfo(payload, data);

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = '210mm';
    tempDiv.style.backgroundColor = 'white';
    document.body.appendChild(tempDiv);

    await new Promise(resolve => setTimeout(resolve, 100));

    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      width: 794,
      scrollX: 0,
      scrollY: 0,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

    const baseFileName = `${infoComprobante.nombreArchivoBase}_${infoComprobante.letra}_${infoComprobante.puntoVenta}-${infoComprobante.numero}`;
    const caeSegment = (infoComprobante.cae || (data && data.cae) || '').toString().trim();
    const caePart = caeSegment ? `_${caeSegment}` : '';
    const filename = `${baseFileName}${caePart}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(filename);

    document.body.removeChild(tempDiv);
  } catch (error) {
    console.error('Error al generar PDF:', error);
    throw new Error('No se pudo generar el PDF de la factura');
  }
}

export async function generarFacturaPdfBlob(data, payload, getParametrosGlobales) {
  try {
    const { jsPDF } = await import('jspdf');
    const html2canvas = (await import('html2canvas')).default;

    const htmlContent = await generarFacturaHtml(data, payload, getParametrosGlobales);

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = '210mm';
    tempDiv.style.backgroundColor = 'white';
    document.body.appendChild(tempDiv);

    await new Promise(resolve => setTimeout(resolve, 100));

    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      width: 794,
      scrollX: 0,
      scrollY: 0,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

    const pdfBlob = pdf.output('blob');
    document.body.removeChild(tempDiv);
    return pdfBlob;
  } catch (error) {
    console.error('Error al generar PDF blob:', error);
    throw new Error('No se pudo generar el PDF de la factura');
  }
}

export async function abrirFacturaPdf(data, payload, getParametrosGlobales) {
  try {
    const pdfBlob = await generarFacturaPdfBlob(data, payload, getParametrosGlobales);
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
  } catch (error) {
    console.error('Error al abrir PDF:', error);
    await abrirFacturaHtml(data, payload, getParametrosGlobales);
  }
}
